import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaStar, FaBriefcase, FaTools, FaShieldAlt } from 'react-icons/fa';
import { SkeletonProfile } from '../components/Skeleton';
import SEOHead from '../components/SEOHead';
import api from '../services/api';

interface PublicWorker {
  id: number;
  first_name: string;
  last_initial: string;
  location: string;
  bio: string;
  skills: string[];
  avatar: string;
  created_at: string;
  primary_role: string;
  secondary_roles: string[];
  union_status: string;
  rate_min: number;
  rate_max: number;
  rate_type: string;
  certifications: string[];
  equipment_proficiencies: string[];
  work_history: { company: string; role: string; year: number }[];
  travel_willing: boolean;
  avg_rating: number;
  review_count: number;
  is_public: boolean;
}

const roleLabels: Record<string, string> = {
  stagehand: 'Stagehand', av_technician: 'AV Technician', lighting_tech: 'Lighting Tech',
  sound_engineer: 'Sound Engineer', gaffer: 'Gaffer', grip: 'Grip', key_grip: 'Key Grip',
  rigger: 'Rigger', camera_operator: 'Camera Operator', production_assistant: 'Production Assistant',
  set_decorator: 'Set Decorator', prop_master: 'Prop Master', wardrobe: 'Wardrobe', carpenter: 'Scenic Carpenter',
};
const formatRole = (role: string) => roleLabels[role] || role?.replace(/_/g, ' ') || 'Crew Member';
const PRIVATE_PROFILE_TITLE = 'Worker profile | StageCrew';
const PRIVATE_PROFILE_DESCRIPTION = 'Worker-provided profile details for signed-in StageCrew employers.';
const PRIVATE_PROFILE_ROBOTS = 'noindex,nofollow';

const PublicWorkerProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const [worker, setWorker] = useState<PublicWorker | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const activeRequest = useRef(0);

  useEffect(() => {
    const requestId = activeRequest.current + 1;
    activeRequest.current = requestId;
    let stillCurrent = true;
    const canCommit = () => stillCurrent && activeRequest.current === requestId;

    const fetchWorker = async () => {
      setLoading(true);
      setWorker(null);
      setNotFound(false);
      setLoadFailed(false);
      try {
        const res = await api.get(`/workers/${id}`);
        if (canCommit()) setWorker(res.data);
      } catch (err: any) {
        if (!canCommit()) return;
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setLoadFailed(true);
        }
      } finally {
        if (canCommit()) setLoading(false);
      }
    };
    fetchWorker();
    return () => {
      stillCurrent = false;
    };
  }, [id, loadAttempt]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={PRIVATE_PROFILE_TITLE}
        description={PRIVATE_PROFILE_DESCRIPTION}
        canonical={false}
        robots={PRIVATE_PROFILE_ROBOTS}
      />
      <div className="max-w-4xl mx-auto px-4 py-8"><SkeletonProfile /></div>
    </div>
  );

  // The employer-only API deliberately returns 404 for both missing and hidden
  // workers. Fail closed if an unexpected response ever includes is_public=false.
  if (notFound || (worker && !worker.is_public)) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <SEOHead title="Worker not found | StageCrew" description="This StageCrew worker profile is not available." canonical={false} robots={PRIVATE_PROFILE_ROBOTS} />
      <div className="text-center"><h1 className="text-2xl font-bold text-gray-700 mb-2">Worker not found</h1><Link to="/employer" className="text-blue-600 hover:underline">Return to employer dashboard</Link></div>
    </div>
  );

  if (loadFailed || !worker) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <SEOHead title="Profile temporarily unavailable | StageCrew" description="StageCrew could not load this worker profile right now." canonical={false} robots={PRIVATE_PROFILE_ROBOTS} />
      <div className="max-w-lg text-center">
        <h1 className="text-2xl font-bold text-gray-700 mb-2">Profile temporarily unavailable</h1>
        <p role="alert" className="text-gray-600 mb-6">We couldn&apos;t load this worker profile right now. Try again or return to your employer dashboard.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button type="button" onClick={() => setLoadAttempt(attempt => attempt + 1)} className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">Try again</button>
          <Link to="/employer" className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">Employer dashboard</Link>
        </div>
      </div>
    </div>
  );

  const displayName = `${worker.first_name} ${worker.last_initial}.`;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={PRIVATE_PROFILE_TITLE}
        description={PRIVATE_PROFILE_DESCRIPTION}
        canonical={false}
        robots={PRIVATE_PROFILE_ROBOTS}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="bg-blue-600 text-white p-8">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-5">
                <div className="w-20 h-20 rounded-full bg-blue-800 flex items-center justify-center text-2xl font-bold flex-shrink-0">
                  {worker.first_name?.charAt(0) || '?'}
                </div>
                <div>
                  <h1 className="text-2xl font-bold">{displayName}</h1>
                  <p className="text-blue-200 capitalize mt-0.5">{formatRole(worker.primary_role)}</p>
                  {worker.location && <p className="flex items-center mt-1 text-sm"><FaMapMarkerAlt className="mr-1" /> {worker.location}</p>}
                  {worker.avg_rating > 0 && <p className="flex items-center mt-1 text-sm"><FaStar className="mr-1 text-yellow-300" />{Number(worker.avg_rating).toFixed(1)} ({worker.review_count} reviews)</p>}
                </div>
              </div>
              <Link to="/post-gig" className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 text-sm whitespace-nowrap">Post a gig</Link>
            </div>
          </div>

          <div className="p-8 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-6">
              {worker.bio && <div><h2 className="text-lg font-bold mb-2">About</h2><p className="text-gray-700">{worker.bio}</p></div>}
              {worker.skills && worker.skills.length > 0 && (
                <div><h2 className="text-lg font-bold mb-2">Skills</h2>
                  <div className="flex flex-wrap gap-2">{worker.skills.map((s, i) => <span key={i} className="bg-blue-50 text-blue-800 border border-blue-100 px-3 py-1 rounded-full text-sm">{s}</span>)}</div>
                </div>
              )}
              {worker.equipment_proficiencies && worker.equipment_proficiencies.length > 0 && (
                <div><h2 className="text-lg font-bold mb-2 flex items-center"><FaTools className="mr-2 text-gray-400" /> Equipment</h2>
                  <div className="flex flex-wrap gap-2">{worker.equipment_proficiencies.map((eq, i) => <span key={i} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">{eq}</span>)}</div>
                </div>
              )}
              {worker.work_history && worker.work_history.length > 0 && (
                <div><h2 className="text-lg font-bold mb-2 flex items-center"><FaBriefcase className="mr-2 text-gray-400" /> Work History</h2>
                  <div className="space-y-2">{worker.work_history.slice(0, 5).map((job, i) => (
                    <div key={i} className="flex justify-between text-sm"><span className="font-medium">{job.role}</span><span className="text-gray-500">{job.company}{job.year && ` · ${job.year}`}</span></div>
                  ))}</div>
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-5">
                <h2 className="font-bold mb-3">Details</h2>
                <div className="space-y-3 text-sm">
                  {worker.rate_min && <div><span className="text-gray-500">Rate</span><p className="font-medium">${worker.rate_min}{worker.rate_max && worker.rate_max !== worker.rate_min ? `–$${worker.rate_max}` : ''}/{worker.rate_type || 'hr'}</p></div>}
                  {worker.union_status && <div><span className="text-gray-500">Union</span><p className="font-medium capitalize">{worker.union_status.replace(/_/g, ' ')}</p></div>}
                  {worker.secondary_roles && worker.secondary_roles.length > 0 && <div><span className="text-gray-500">Also works as</span><p className="font-medium">{worker.secondary_roles.map(formatRole).join(', ')}</p></div>}
                  {worker.travel_willing && <div className="flex items-center text-green-700"><FaShieldAlt className="mr-2" /><span>Open to travel</span></div>}
                  {worker.certifications && worker.certifications.length > 0 && <div><span className="text-gray-500">Certifications</span><p className="font-medium">{worker.certifications.join(', ')}</p></div>}
                  <div className="text-gray-400 text-xs pt-2">Member since {new Date(worker.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                </div>
              </div>
              <div className="bg-blue-600 text-white rounded-lg p-5 text-center">
                <p className="font-semibold mb-1">Need crew for your next event?</p>
                <p className="text-blue-200 text-sm mb-3">Review {worker.first_name}&apos;s worker-provided profile and browse other available crew profiles.</p>
                <Link to="/post-gig" className="block bg-white text-blue-600 px-4 py-2 rounded font-semibold hover:bg-gray-100 text-sm">Post a gig</Link>
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-sm text-gray-400 mt-6">
          <Link to="/" className="hover:underline">StageCrew</Link>{' › '}<Link to="/employer" className="hover:underline">Employer dashboard</Link>{' › '}{displayName}
        </p>
      </div>
    </div>
  );
};

export default PublicWorkerProfilePage;
